import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from './contexts/AuthContext';
import WarningLettersPipeline from './WarningLettersPipeline';
import './DataExplorer.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const BASE = `${API_URL}/api/v1/admin/data-explorer`;

/* ====================================================================
   Shared: DataTable
   ==================================================================== */
function DataTable({ columns, data, onRowClick, emptyText }) {
  return (
    <table className="de-table">
      <thead>
        <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr>
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr key={row.id || i} onClick={() => onRowClick && onRowClick(row)}
            className={onRowClick ? 'clickable' : ''}>
            {columns.map(c => (
              <td key={c.key} className={c.className || ''}>
                {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '–')}
              </td>
            ))}
          </tr>
        ))}
        {data.length === 0 && (
          <tr><td colSpan={columns.length} className="de-empty">{emptyText || 'No data'}</td></tr>
        )}
      </tbody>
    </table>
  );
}

/* ====================================================================
   Shared: Pagination
   ==================================================================== */
function Pagination({ page, total, perPage, setPage }) {
  const pages = Math.ceil(total / perPage);
  if (pages <= 1) return null;
  return (
    <div className="de-pagination">
      <button disabled={page <= 1} onClick={() => setPage(page - 1)}>◀ Prev</button>
      <span>Page {page} of {pages} ({total} items)</span>
      <button disabled={page >= pages} onClick={() => setPage(page + 1)}>Next ▶</button>
    </div>
  );
}

/* ====================================================================
   Shared: ProgressBar
   ==================================================================== */
function ProgressBar({ current, target, label, color }) {
  const pct = target > 0 ? Math.min(Math.round(current / target * 100), 100) : 0;
  return (
    <div className="progress-item">
      <div className="progress-label">
        <span>{label}</span>
        <span>{current.toLocaleString()} / {target.toLocaleString()} ({pct}%)</span>
      </div>
      <div className="progress-track">
        <div className="progress-fill" style={{ width: `${pct}%`, background: color || '#1a73e8' }} />
      </div>
    </div>
  );
}

/* ====================================================================
   Shared: ConfusionMatrix
   ==================================================================== */
function ConfusionMatrix({ matrix, classes }) {
  if (!matrix || !classes) return null;
  const maxVal = Math.max(1, ...classes.flatMap(a => classes.map(e => matrix[a]?.[e] || 0)));
  return (
    <div className="confusion-matrix">
      <h4>Confusion Matrix</h4>
      <table className="cm-table">
        <thead>
          <tr><th></th><th colSpan={classes.length}>Expected</th></tr>
          <tr><th>Actual ↓</th>{classes.map(c => <th key={c}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {classes.map(actual => (
            <tr key={actual}>
              <th>{actual}</th>
              {classes.map(expected => {
                const v = matrix[actual]?.[expected] || 0;
                const intensity = Math.round(v / maxVal * 0.6 * 255);
                const bg = actual === expected
                  ? `rgba(76, 175, 80, ${v / maxVal * 0.7})`
                  : `rgba(244, 67, 54, ${v / maxVal * 0.5})`;
                return <td key={expected} style={{ background: bg }}>{v}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ====================================================================
   Tab 1: Regulations Browser
   ==================================================================== */
function RegulationsTab() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState(null);

  useEffect(() => {
    fetch(`${BASE}/regulations`).then(r => r.json()).then(setData).catch(console.error);
  }, []);

  const doSearch = async () => {
    if (!search.trim()) { setResults(null); return; }
    const r = await fetch(`${BASE}/regulations/search?q=${encodeURIComponent(search)}`).then(r => r.json());
    setResults(r);
  };

  const linkIcon = (url) => url ? <a href={url} target="_blank" rel="noreferrer" className="ext-link">🔗</a> : null;
  const usedByBadges = (agents) => (agents || []).map(a => <span key={a} className="used-by-badge">{a}</span>);

  if (!data) return <div className="de-loading">Loading regulations…</div>;

  const display = results || null;

  return (
    <div className="tab-content">
      <div className="search-bar">
        <input type="text" placeholder="Search regulations…" value={search}
          onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && doSearch()} />
        <button onClick={doSearch} className="btn-primary">Search</button>
        {results && <button onClick={() => setResults(null)} className="btn-secondary">Clear</button>}
      </div>

      {display ? (
        <div className="search-results">
          <h3>{display.length} result(s)</h3>
          {display.map((r, i) => (
            <div key={i} className="reg-card">
              <strong>{r.section || r.monograph || r.title || r.source}</strong>
              <span className="reg-source">{r._source}</span>
              <p>{r.description || r.when_to_cite || ''}</p>
              {r.url && linkIcon(r.url)}
              {r.used_by && <div>{usedByBadges(r.used_by)}</div>}
            </div>
          ))}
        </div>
      ) : (
        <>
          <section>
            <h3>FD&C Act Sections</h3>
            <DataTable
              columns={[
                { key: 'section', label: 'Section' },
                { key: 'title', label: 'Title' },
                { key: 'description', label: 'Description', className: 'desc-col' },
                { key: 'when_to_cite', label: 'When to Cite', className: 'desc-col' },
                { key: 'url', label: '', render: (v) => linkIcon(v) },
                { key: 'used_by', label: 'Used By', render: (v) => usedByBadges(v) },
              ]}
              data={data.fdc_act_sections}
            />
          </section>

          <section>
            <h3>21 CFR Sections</h3>
            <DataTable
              columns={[
                { key: 'section', label: 'Section' },
                { key: 'title', label: 'Title' },
                { key: 'description', label: 'Description', className: 'desc-col' },
                { key: 'url', label: '', render: (v) => linkIcon(v) },
                { key: 'used_by', label: 'Used By', render: (v) => usedByBadges(v) },
              ]}
              data={data.cfr_sections}
            />
          </section>

          <section>
            <h3>OTC Monographs</h3>
            <DataTable
              columns={[
                { key: 'monograph', label: 'Monograph' },
                { key: 'category', label: 'Category' },
                { key: 'active_ingredients', label: 'Active Ingredients', render: (v) => (v||[]).join(', ') },
                { key: 'allowed_conditions', label: 'Allowed Conditions', render: (v) => (v||[]).join(', ') },
              ]}
              data={data.otc_monographs}
            />
          </section>

          <section>
            <h3>MoCRA 2022</h3>
            <div className="reg-card">
              <strong>{data.mocra_2022.title}</strong>
              <p>{data.mocra_2022.description}</p>
              <DataTable
                columns={[
                  { key: 'provision', label: 'Provision' },
                  { key: 'description', label: 'Description' },
                  { key: 'effective', label: 'Effective Date' },
                ]}
                data={data.mocra_2022.key_provisions}
              />
            </div>
          </section>

          <section>
            <h3>Industry Guidelines</h3>
            <DataTable
              columns={[
                { key: 'source', label: 'Source' },
                { key: 'title', label: 'Title' },
                { key: 'description', label: 'Description', className: 'desc-col' },
                { key: 'url', label: '', render: (v) => linkIcon(v) },
              ]}
              data={data.industry_guidelines}
            />
          </section>
        </>
      )}
    </div>
  );
}

/* ====================================================================
   Tab 2: Keyword Dictionaries
   ==================================================================== */
function KeywordDictionariesTab() {
  const [dicts, setDicts] = useState(null);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/keywords`).then(r => r.json()),
      fetch(`${BASE}/keywords/stats`).then(r => r.json()),
    ]).then(([d, s]) => { setDicts(d); setStats(s); }).catch(console.error);
  }, []);

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const doExport = async (fmt) => {
    window.open(`${BASE}/keywords/export?format=${fmt}`, '_blank');
  };

  if (!dicts) return <div className="de-loading">Loading keywords…</div>;

  const filtered = Object.entries(dicts).filter(([key, info]) => {
    if (!search) return true;
    const q = search.toLowerCase();
    if (info.category.toLowerCase().includes(q)) return true;
    return info.terms.some(t => t.toLowerCase().includes(q));
  });

  return (
    <div className="tab-content">
      <div className="kw-header">
        <div className="search-bar">
          <input type="text" placeholder="Search keywords…" value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="kw-actions">
          <span className="kw-total">Total keywords: {stats?._total || 0}</span>
          <button onClick={() => doExport('csv')} className="btn-secondary">Export CSV</button>
          <button onClick={() => doExport('json')} className="btn-secondary">Export JSON</button>
        </div>
      </div>
      {filtered.map(([key, info]) => {
        const terms = search
          ? info.terms.filter(t => t.toLowerCase().includes(search.toLowerCase()))
          : info.terms;
        const isOpen = expanded[key] || !!search;
        return (
          <div key={key} className="kw-section">
            <div className="kw-section-header" onClick={() => toggle(key)}>
              <span className="kw-toggle">{isOpen ? '▾' : '▸'}</span>
              <span className="kw-category">{info.category}</span>
              <span className={`kw-risk kw-risk-${info.risk_level}`}>{info.risk_level}</span>
              <span className="kw-count">{terms.length} terms</span>
            </div>
            {isOpen && (
              <div className="kw-terms">
                <p className="kw-desc">{info.description}</p>
                <div className="kw-term-list">
                  {terms.map((t, i) => (
                    <span key={i} className="kw-term">{t}</span>
                  ))}
                  {terms.length === 0 && <span className="kw-empty">No matching terms</span>}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ====================================================================
   Tab 3: Vector Indexes
   ==================================================================== */
function VectorIndexesTab() {
  const [index, setIndex] = useState('drug-precedent');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [vStats, setVStats] = useState({});
  const [simQuery, setSimQuery] = useState('');
  const [simResults, setSimResults] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [list, stats] = await Promise.all([
        fetch(`${BASE}/vectors/${index}?page=${page}&per_page=20`).then(r => r.json()),
        fetch(`${BASE}/vectors/stats/all`).then(r => r.json()),
      ]);
      setItems(list.items || []);
      setTotal(list.total || 0);
      setVStats(stats);
    } catch (e) { console.error('Failed to load vector indexes', e); }
  }, [index, page]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh stats every 60 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const stats = await fetch(`${BASE}/vectors/stats/all`).then(r => r.json());
        setVStats(stats);
      } catch (e) { /* ignore refresh errors */ }
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const runSimilarity = async () => {
    if (!simQuery.trim()) return;
    setSimLoading(true);
    try {
      const key = index.replace('-', '_');
      const r = await fetch(`${BASE}/vectors/similarity-test?query=${encodeURIComponent(simQuery)}&index_name=${key}&top_k=5`, { method: 'POST' });
      const data = await r.json();
      setSimResults(data.results);
    } finally { setSimLoading(false); }
  };

  const reEmbed = async () => {
    await fetch(`${BASE}/vectors/re-embed?index_name=${index}`, { method: 'POST' });
    await load();
  };

  const indexKey = index.replace('-', '_');
  const indexStat = vStats[indexKey] || {};

  return (
    <div className="tab-content">
      <div className="vi-selector">
        {['drug-precedent', 'cosmetic-safe', 'otc-monograph'].map(idx => (
          <button key={idx} className={index === idx ? 'tab active' : 'tab'}
            onClick={() => { setIndex(idx); setPage(1); setSimResults(null); }}>
            {idx.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </button>
        ))}
      </div>

      <div className="vi-stats">
        <span>{(indexStat.total || 0).toLocaleString()} entries</span>
        <span>{(indexStat.embedded || 0).toLocaleString()} embedded</span>
        {indexStat.total > 0 && indexStat.embedded < indexStat.total && (
          <button onClick={reEmbed} className="btn-secondary">Re-embed missing</button>
        )}
      </div>

      {(indexStat.by_source || []).length > 0 && (
        <div className="vi-composition" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', margin: '2px 0 14px' }}>
          <span style={{ fontSize: 12, color: '#888' }}>Composition:</span>
          {indexStat.by_source.map(s => (
            <span key={s.source} style={{ fontSize: 12, background: 'var(--bg-secondary,#f0f0f0)', borderRadius: 12, padding: '2px 9px', whiteSpace: 'nowrap' }}>
              {s.source} <strong>{(s.count || 0).toLocaleString()}</strong>
            </span>
          ))}
        </div>
      )}

      <DataTable
        columns={[
          { key: 'claim_text', label: 'Claim Text', className: 'desc-col' },
          { key: 'source', label: 'Source' },
          { key: 'has_embedding', label: 'Embedded', render: v => v ? '✅' : '❌' },
          { key: 'created_at', label: 'Created', render: v => v ? new Date(v).toLocaleDateString() : '–' },
        ]}
        data={items}
        emptyText="No entries in this index. Run the population script or trigger ingestion from the Warning Letters pipeline."
      />
      <Pagination page={page} total={total} perPage={20} setPage={setPage} />

      <div className="similarity-tester">
        <h4>Similarity Tester</h4>
        <div className="sim-row">
          <input type="text" placeholder="Enter a claim to test…" value={simQuery}
            onChange={e => setSimQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runSimilarity()} />
          <button onClick={runSimilarity} disabled={simLoading} className="btn-primary">
            {simLoading ? 'Searching…' : 'Search'}
          </button>
        </div>
        {simResults && (
          <div className="sim-results">
            {simResults.map((r, i) => (
              <div key={i} className="sim-result">
                <div className="sim-score-bar">
                  <div className="sim-fill" style={{ width: `${Math.round(r.similarity * 100)}%` }} />
                  <span className="sim-score">{(r.similarity * 100).toFixed(1)}%</span>
                </div>
                <div className="sim-text">{r.claim_text}</div>
              </div>
            ))}
            {simResults.length === 0 && <p className="de-empty">No similar entries found</p>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ====================================================================
   Tab 4: Test Cases
   ==================================================================== */
function TestCasesTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [chanFilter, setChanFilter] = useState('');
  const [tcStats, setTcStats] = useState({});
  const [runResults, setRunResults] = useState(null);
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ page, per_page: 20 });
      if (filter) qs.set('classification', filter);
      if (chanFilter) qs.set('channel', chanFilter);
      const [list, stats] = await Promise.all([
        fetch(`${BASE}/test-cases?${qs}`).then(r => r.json()),
        fetch(`${BASE}/test-cases/stats`).then(r => r.json()),
      ]);
      setItems(list.items || []);
      setTotal(list.total || 0);
      setTcStats(stats);
    } catch (e) { console.error('Failed to load test cases', e); }
  }, [page, filter, chanFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const runSelected = async () => {
    setRunning(true);
    try {
      const ids = [...selected];
      const qs = ids.length > 0 ? ids.map(id => `test_ids=${id}`).join('&') : `limit=40`;
      const r = await fetch(`${BASE}/test-cases/run?${qs}`, { method: 'POST' });
      const data = await r.json();
      setRunResults(data);
    } finally { setRunning(false); }
  };

  const catBadge = (cat) => {
    const colors = { DRUG: '#f44336', OTC: '#ff9800', COSMETIC: '#4caf50' };
    return <span className="de-badge" style={{ background: colors[cat] || '#999', color: '#fff' }}>{cat}</span>;
  };

  return (
    <div className="tab-content">
      <div className="tc-filters">
        <label>Classification:
          <select value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }}>
            <option value="">All</option><option value="DRUG">Drug</option>
            <option value="OTC">OTC</option><option value="COSMETIC">Cosmetic</option>
          </select>
        </label>
        <label>Channel:
          <select value={chanFilter} onChange={e => { setChanFilter(e.target.value); setPage(1); }}>
            <option value="">All</option>
            {Object.keys(tcStats.by_channel || {}).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <span className="tc-total">Total: {tcStats.total || 0}</span>
        <button onClick={runSelected} disabled={running} className="btn-primary">
          {running ? 'Running…' : selected.size > 0 ? `Run Selected (${selected.size})` : 'Run All (20)'}
        </button>
      </div>

      <table className="de-table">
        <thead>
          <tr><th></th><th>ID</th><th>Channel</th><th>Claim</th><th>Expected</th><th>Risk</th></tr>
        </thead>
        <tbody>
          {items.map(r => (
            <tr key={r.id}>
              <td><input type="checkbox" checked={selected.has(String(r.id))}
                onChange={() => toggleSelect(String(r.id))} /></td>
              <td>{r.test_id || '–'}</td>
              <td>{r.channel}</td>
              <td className="desc-col">{r.input_text}</td>
              <td>{catBadge(r.expected_category)}</td>
              <td>{r.expected_risk || '–'}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={6} className="de-empty">No test cases</td></tr>}
        </tbody>
      </table>
      <Pagination page={page} total={total} perPage={20} setPage={setPage} />

      {runResults && (
        <div className="run-results">
          <h3>Results: {runResults.accuracy}% accuracy ({runResults.passed}/{runResults.total} passed)</h3>
          {runResults.metrics && (
            <>
              <div className="metrics-row">
                {['DRUG', 'OTC', 'COSMETIC'].map(c => (
                  <div key={c} className="metric-card">
                    <strong>{c}</strong>
                    <span>Precision: {((runResults.metrics.precision?.[c] || 0) * 100).toFixed(1)}%</span>
                    <span>Recall: {((runResults.metrics.recall?.[c] || 0) * 100).toFixed(1)}%</span>
                  </div>
                ))}
              </div>
              <ConfusionMatrix matrix={runResults.metrics.confusion_matrix} classes={runResults.metrics.classes} />
            </>
          )}
          <table className="de-table">
            <thead>
              <tr><th>ID</th><th>Claim</th><th>Expected</th><th>Actual</th><th>Result</th><th>Conf.</th></tr>
            </thead>
            <tbody>
              {runResults.results.map((r, i) => (
                <tr key={i} className={r.passed ? 'row-pass' : 'row-fail'}>
                  <td>{r.test_id}</td>
                  <td className="desc-col">{r.input_text}</td>
                  <td>{catBadge(r.expected_category)}</td>
                  <td>{catBadge(r.actual_category)}</td>
                  <td>{r.passed ? '✅ Pass' : '❌ Fail'}</td>
                  <td>{r.confidence ? `${Math.round(r.confidence * 100)}%` : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ====================================================================
   Tab 5: Seed Data
   ==================================================================== */
function SeedDataTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState('');
  const [sdStats, setSdStats] = useState({});
  const [viewText, setViewText] = useState(null);

  const load = useCallback(async () => {
    try {
      const qs = `page=${page}&per_page=20${category ? `&category=${category}` : ''}`;
      const [list, stats] = await Promise.all([
        fetch(`${BASE}/seed-data/warning-letters?${qs}`).then(r => r.json()),
        fetch(`${BASE}/seed-data/stats`).then(r => r.json()),
      ]);
      setItems(list.items || []);
      setTotal(list.total || 0);
      setSdStats(stats);
    } catch (e) { console.error('Failed to load seed data', e); }
  }, [page, category]);

  useEffect(() => { load(); }, [load]);

  const viewLetter = async (id) => {
    const data = await fetch(`${BASE}/seed-data/warning-letters/${id}`).then(r => r.json());
    setViewText(data.text || 'No text available');
  };

  const wl = sdStats.warning_letters || {};
  const drug = sdStats.drug_precedent_index || {};
  const cosmetic = sdStats.cosmetic_safe_index || {};
  const otc = sdStats.otc_monograph_index || {};
  const ingredients = sdStats.ingredient_catalog || {};
  const rules = sdStats.rule_dictionaries || {};
  const testCases = sdStats.test_cases || {};
  const labeled = sdStats.labeled_dataset || {};
  const pipeline = sdStats.pipeline_runs || {};

  const summaryCards = [
    { label: 'Drug Precedent Claims', count: drug.total || 0, embedded: drug.embedded || 0, target: 12000, color: '#f44336' },
    { label: 'Cosmetic Safe Claims', count: cosmetic.total || 0, embedded: cosmetic.embedded || 0, target: 3000, color: '#4caf50' },
    { label: 'OTC Monograph Claims', count: otc.total || 0, embedded: otc.embedded || 0, target: 12000, color: '#ff9800' },
    { label: 'Warning Letters', count: wl.total || 0, embedded: wl.embedded || 0, target: 500, color: '#9c27b0' },
    { label: 'Ingredient Catalog', count: ingredients.total || 0, detail: `${ingredients.families || 0} families`, color: '#2196f3' },
    { label: 'Rule Dictionary Terms', count: rules.total_terms || 0, detail: `${rules.categories || 0} categories`, color: '#795548' },
    { label: 'Test Cases', count: (testCases.default || 0) + (testCases.custom || 0), detail: `${testCases.default || 0} default / ${testCases.custom || 0} custom`, color: '#607d8b' },
    { label: 'Pipeline Runs', count: pipeline.total || 0, detail: `${pipeline.successful || 0} successful`, color: '#00bcd4' },
  ];

  return (
    <div className="tab-content">
      {/* Summary Cards */}
      <div className="sd-summary-grid">
        {summaryCards.map(card => {
          const pct = card.target ? Math.min(Math.round((card.count / card.target) * 100), 100) : null;
          return (
            <div key={card.label} className="sd-summary-card">
              <div className="sd-card-header" style={{ borderLeftColor: card.color }}>
                <span className="sd-card-label">{card.label}</span>
                <span className="sd-card-count">{card.count.toLocaleString()}</span>
              </div>
              {card.embedded !== undefined && card.target && (
                <div className="sd-card-progress">
                  <div className="sd-progress-bar">
                    <div className="sd-progress-fill" style={{ width: `${pct}%`, backgroundColor: card.color }} />
                  </div>
                  <span className="sd-progress-text">{pct}% of {card.target.toLocaleString()} target</span>
                </div>
              )}
              {card.embedded !== undefined && <div className="sd-card-detail">Embedded: {card.embedded.toLocaleString()}</div>}
              {card.detail && <div className="sd-card-detail">{card.detail}</div>}
            </div>
          );
        })}
      </div>

      {/* Dataset Release */}
      {labeled.version && (
        <div className="sd-release-info">
          <strong>Dataset Release:</strong> v{labeled.version} — {labeled.total || 0} labeled rows — Profile: {labeled.profile || 'N/A'}
        </div>
      )}

      {/* Warning Letters Table */}
      <h3 style={{ marginTop: 24 }}>FDA Warning Letters</h3>
      <div className="sd-header">
        <span>Total: {wl.total || 0}</span>
        <span>Embedded: {wl.embedded || 0}</span>
        <label>Category:
          <select value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}>
            <option value="">All</option>
            {Object.keys(wl.by_category || {}).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>
      <DataTable
        columns={[
          { key: 'letter_id', label: 'Letter ID' },
          { key: 'company_name', label: 'Company' },
          { key: 'issue_date', label: 'Date', render: v => v || '–' },
          { key: 'category', label: 'Category' },
          { key: 'text_preview', label: 'Preview', className: 'desc-col' },
          { key: 'has_embedding', label: 'Embedded', render: v => v ? '✅' : '❌' },
        ]}
        data={items}
        onRowClick={(row) => viewLetter(row.id)}
        emptyText="No warning letters ingested. Use the Warning Letters pipeline to fetch from FDA."
      />
      <Pagination page={page} total={total} perPage={20} setPage={setPage} />
      {viewText && (
        <div className="raw-text-overlay" onClick={() => setViewText(null)}>
          <div className="raw-text-panel" onClick={e => e.stopPropagation()}>
            <div className="raw-text-header">
              <span>Letter Text</span><button onClick={() => setViewText(null)}>✕</button>
            </div>
            <pre className="raw-text-content">{viewText}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================================================================
   Tab 6: System Config & Health
   ==================================================================== */
function SystemConfigTab() {
  const [ensemble, setEnsemble] = useState(null);
  const [veto, setVeto] = useState(null);
  const [scoring, setScoring] = useState(null);
  const [embModel, setEmbModel] = useState(null);
  const [health, setHealth] = useState(null);
  const [progress, setProgress] = useState(null);
  const [perf, setPerf] = useState(null);
  const [pipelineConfig, setPipelineConfig] = useState(null);
  const [mlStatus, setMlStatus] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/config/ensemble`).then(r => r.json()),
      fetch(`${BASE}/config/veto-rules`).then(r => r.json()),
      fetch(`${BASE}/config/scoring`).then(r => r.json()),
      fetch(`${BASE}/config/embedding-model`).then(r => r.json()),
      fetch(`${BASE}/health`).then(r => r.json()),
      fetch(`${BASE}/health/dataset-progress`).then(r => r.json()),
      fetch(`${BASE}/health/performance`).then(r => r.json()),
      fetch(`${BASE}/config/pipeline`).then(r => r.json()).catch(() => null),
      fetch(`${BASE}/config/ml-status`).then(r => r.json()).catch(() => null),
    ]).then(([e, v, s, em, h, p, pf, pc, ml]) => {
      setEnsemble(e); setVeto(v); setScoring(s); setEmbModel(em);
      setHealth(h); setProgress(p); setPerf(pf); setPipelineConfig(pc);
      setMlStatus(ml);
    }).catch(console.error);
  }, []);

  // Auto-refresh health and progress every 60 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const [h, p] = await Promise.all([
          fetch(`${BASE}/health`).then(r => r.json()),
          fetch(`${BASE}/health/dataset-progress`).then(r => r.json()),
        ]);
        setHealth(h);
        setProgress(p);
      } catch (e) { /* ignore refresh errors */ }
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  if (!health) return <div className="de-loading">Loading system health…</div>;

  return (
    <div className="tab-content sc-tab">
      {/* Ensemble Weights */}
      <section className="sc-section">
        <h3>Ensemble Signal Weights</h3>
        {ensemble && (
          <div className="sc-cards">
            <div className={`sc-card${ensemble.current_mode === 'with_ml' ? ' active' : ''}`}>
              <h4>With ML{ensemble.current_mode === 'with_ml' ? ' (current)' : ''}</h4>
              {Object.entries(ensemble.weights_with_ml).map(([k, v]) => (
                <div key={k} className="sc-weight">{k}: <strong>{(v * 100).toFixed(0)}%</strong></div>
              ))}
            </div>
            <div className={`sc-card${ensemble.current_mode !== 'with_ml' ? ' active' : ''}`}>
              <h4>Without ML{ensemble.current_mode !== 'with_ml' ? ' (current)' : ''}</h4>
              {Object.entries(ensemble.weights_without_ml).map(([k, v]) => (
                <div key={k} className="sc-weight">{k}: <strong>{(v * 100).toFixed(0)}%</strong></div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ML Classifier Status */}
      <section className="sc-section">
        <h3>ML Classifier</h3>
        {mlStatus && (
          <>
            <div className="sc-cards">
              <div className={`sc-card${mlStatus.ml_enabled ? ' active' : ''}`}>
                <h4>Status</h4>
                <div>Enabled: <strong>{mlStatus.ml_enabled ? '✅ Yes' : '❌ No'}</strong></div>
                <div>Model loaded: <strong>{mlStatus.ready ? '✅ Yes' : '❌ No'}</strong></div>
              </div>
              {mlStatus.ready && (
                <div className="sc-card">
                  <h4>Active Model</h4>
                  <div>Type: <strong>{mlStatus.model_type || 'N/A'}</strong></div>
                  <div>Version: <strong>{mlStatus.model_version || 'N/A'}</strong></div>
                  <div>Dataset: <strong>{mlStatus.dataset_version || 'N/A'}</strong></div>
                  <div>Macro F1: <strong>{mlStatus.macro_f1 != null ? mlStatus.macro_f1.toFixed(4) : 'N/A'}</strong></div>
                </div>
              )}
            </div>
            {mlStatus.all_models && mlStatus.all_models.length > 1 && (
              <div style={{marginTop:'1rem'}}>
                <h4 style={{marginBottom:'0.5rem'}}>Available Models</h4>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.875rem'}}>
                  <thead>
                    <tr style={{background:'var(--bg-secondary,#f0f0f0)'}}>
                      <th style={{padding:'6px 10px',textAlign:'left'}}>Directory</th>
                      <th style={{padding:'6px 10px',textAlign:'left'}}>Type</th>
                      <th style={{padding:'6px 10px',textAlign:'center'}}>Macro F1</th>
                      <th style={{padding:'6px 10px',textAlign:'left'}}>Dataset</th>
                      <th style={{padding:'6px 10px',textAlign:'center'}}>Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mlStatus.all_models.map(m => (
                      <tr key={m.directory} style={{borderTop:'1px solid var(--border,#ddd)',background: m.model_type === mlStatus.model_type ? 'var(--accent-light,#e8f4e8)' : ''}}>
                        <td style={{padding:'5px 10px',fontFamily:'monospace'}}>{m.directory}</td>
                        <td style={{padding:'5px 10px'}}>{m.model_type || '—'}</td>
                        <td style={{padding:'5px 10px',textAlign:'center'}}>{m.macro_f1 != null ? m.macro_f1.toFixed(4) : '—'}</td>
                        <td style={{padding:'5px 10px'}}>{m.dataset_version || '—'}</td>
                        <td style={{padding:'5px 10px',textAlign:'center'}}>{m.model_type === mlStatus.model_type ? '✅' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      {/* Veto Rules */}
      <section className="sc-section">
        <h3>Veto Power Hierarchy</h3>
        {veto?.veto_hierarchy?.map(r => (
          <div key={r.priority} className="veto-rule">
            <span className="veto-pri">P{r.priority}</span>
            <div>
              <strong>{r.rule}</strong>
              <p>{r.description}</p>
              <div className="veto-examples">
                {r.examples.map((e, i) => <code key={i}>{e}</code>)}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Risk Scoring */}
      <section className="sc-section">
        <h3>Risk Scoring Thresholds</h3>
        {scoring && (
          <table className="de-table">
            <thead><tr><th>Level</th><th>Score Range</th><th>Description</th></tr></thead>
            <tbody>
              {Object.entries(scoring.risk_scoring).map(([k, v]) => (
                <tr key={k}><td><span className={`risk-badge risk-${k}`}>{k}</span></td><td>{v.score_range}</td><td>{v.description}</td></tr>
              ))}
            </tbody>
          </table>
        )}
        <h4>Vector Delta Interpretation</h4>
        {scoring && (
          <table className="de-table">
            <thead><tr><th>Signal</th><th>Range</th><th>Interpretation</th></tr></thead>
            <tbody>
              {Object.entries(scoring.vector_delta_thresholds).map(([k, v]) => (
                <tr key={k}><td>{k.replace(/_/g, ' ')}</td><td>{v.range}</td><td>{v.interpretation}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Embedding Model */}
      <section className="sc-section">
        <h3>Embedding Model</h3>
        {embModel && (
          <div className="sc-card">
            <div>Model: <strong>{embModel.model_name}</strong></div>
            <div>Dimensions: <strong>{embModel.dimensions}</strong></div>
            <div>Similarity: <strong>{embModel.similarity_method}</strong></div>
            <div>Batch size: <strong>{embModel.batch_size}</strong></div>
          </div>
        )}
      </section>

      {/* Data Pipeline Schedule */}
      {pipelineConfig && (
        <section className="sc-section">
          <h3>Data Pipeline Schedule</h3>
          {pipelineConfig.ingestion && (
            <>
              <h4>Ingestion Sources ({pipelineConfig.ingestion.enabled}/{pipelineConfig.ingestion.source_count} enabled)</h4>
              <table className="de-table">
                <thead><tr><th>Source</th><th>Interval</th><th>Status</th></tr></thead>
                <tbody>
                  {(pipelineConfig.ingestion.sources || []).map(s => (
                    <tr key={s.id}>
                      <td>{s.family}</td>
                      <td>{s.interval}</td>
                      <td>{s.enabled ? '✅ Enabled' : '❌ Disabled'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {pipelineConfig.pipeline_schedule?.components && (
            <>
              <h4>Pipeline Jobs ({pipelineConfig.pipeline_schedule.component_count} components)</h4>
              <table className="de-table">
                <thead><tr><th>Component</th><th>Schedule</th><th>Status</th></tr></thead>
                <tbody>
                  {Object.entries(pipelineConfig.pipeline_schedule.components).map(([k, v]) => (
                    <tr key={k}>
                      <td>{k.replace(/_/g, ' ')}</td>
                      <td>{v.cron_description || v.cron}</td>
                      <td>{v.enabled ? '✅' : '❌'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {pipelineConfig.alerts && (
            <>
              <h4>Alert Rules ({pipelineConfig.alerts.rule_count} rules)</h4>
              <table className="de-table">
                <thead><tr><th>Rule</th><th>Severity</th><th>Description</th></tr></thead>
                <tbody>
                  {(pipelineConfig.alerts.rules || []).map(r => (
                    <tr key={r.name}>
                      <td>{r.name.replace(/_/g, ' ')}</td>
                      <td><span className={`risk-badge risk-${r.severity === 'critical' ? 'CRITICAL' : r.severity === 'warning' ? 'HIGH' : 'MEDIUM'}`}>{r.severity}</span></td>
                      <td>{r.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>
      )}

      {/* Data Enhancement Artifacts */}
      {pipelineConfig && (
        <section className="sc-section">
          <h3>Data Enhancement Artifacts</h3>
          <div className="sc-cards">
            {pipelineConfig.source_manifest && (
              <div className="sc-card">
                <h4>Source Manifest</h4>
                <div>Version: <strong>{pipelineConfig.source_manifest.version}</strong></div>
                <div>Phase: <strong>{pipelineConfig.source_manifest.phase}</strong></div>
                <div>Profile: <strong>{pipelineConfig.source_manifest.profile}</strong></div>
              </div>
            )}
            {pipelineConfig.ingredient_catalog && (
              <div className="sc-card">
                <h4>Ingredient Catalog</h4>
                <div>Total: <strong>{pipelineConfig.ingredient_catalog.total}</strong></div>
                <div>Families: <strong>{pipelineConfig.ingredient_catalog.families}</strong></div>
              </div>
            )}
            {pipelineConfig.dataset_release && (
              <div className="sc-card">
                <h4>Dataset Release</h4>
                <div>Version: <strong>{pipelineConfig.dataset_release.version}</strong></div>
                <div>Labeled: <strong>{pipelineConfig.dataset_release.total_labeled}</strong></div>
                <div>Profile: <strong>{pipelineConfig.dataset_release.profile}</strong></div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Dataset Progress */}
      <section className="sc-section">
        <h3>Dataset Size Progress</h3>
        {progress && Object.entries(progress).map(([k, v]) => (
          <ProgressBar key={k}
            label={k.replace(/_/g, ' ')}
            current={v.current}
            target={v.target_min}
            color={v.percent >= 100 ? '#4caf50' : v.percent >= 50 ? '#ff9800' : '#f44336'}
          />
        ))}
      </section>

      {/* Database Health */}
      <section className="sc-section">
        <h3>Database Health</h3>
        <div className="sc-status">
          Status: <span className={health.db_status === 'connected' ? 'status-ok' : 'status-err'}>
            {health.db_status}
          </span>
        </div>
        <table className="de-table">
          <thead><tr><th>Table</th><th>Row Count</th></tr></thead>
          <tbody>
            {Object.entries(health.table_counts || {}).map(([k, v]) => (
              <tr key={k}>
                <td>{k}</td>
                <td>{v === -1 ? <span className="status-err">not created</span> : v.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Agent Versions */}
      <section className="sc-section">
        <h3>Agent Versions</h3>
        <table className="de-table">
          <thead><tr><th>Agent</th><th>Version</th></tr></thead>
          <tbody>
            {(health.agents || []).map(a => (
              <tr key={a.name}><td>{a.name}</td><td>{a.version}</td></tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Performance */}
      {perf && (
        <section className="sc-section">
          <h3>Performance</h3>
          <div className="sc-card">
            <div>Recent analyses (1h): <strong>{perf.recent_analyses_1h}</strong></div>
            <div>Avg latency: <strong>{perf.avg_latency_ms ? `${perf.avg_latency_ms}ms` : 'N/A'}</strong></div>
            <div>Latency target: <strong>{perf.targets.latency_target_ms}ms</strong></div>
            <div>Accuracy target: <strong>{perf.targets.accuracy_target_percent}%</strong></div>
          </div>
        </section>
      )}
    </div>
  );
}

/* ====================================================================
   Tab: Recent Ingested — cross-source newest content (B-3.3)
   ==================================================================== */
const SOURCE_LABELS = {
  fda_warning_letters: 'Warning Letter',
  drug_precedent_index: 'Drug Precedent',
  otc_monograph_index: 'OTC Monograph',
  cosmetic_safe_index: 'Cosmetic Safe',
};

function RecentContentTab() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // OFFSET pagination (page/per_page) — matches the backend, which uses OFFSET to
  // avoid dropping rows that share a bulk-seed's identical created_at timestamp.
  const fetchPage = useCallback(async (pg, append) => {
    try {
      const data = await fetch(`${BASE}/recent-content?page=${pg}&per_page=25`).then(r => r.json());
      const list = data.items || [];
      setItems(prev => (append ? [...prev, ...list] : list));
      setHasMore(!!data.has_more);
      setPage(pg);
    } catch (e) { console.error('Failed to load recent content', e); }
  }, []);

  useEffect(() => { fetchPage(1, false); }, [fetchPage]);

  const loadOlder = async () => {
    setLoadingMore(true);
    try { await fetchPage(page + 1, true); }
    finally { setLoadingMore(false); }
  };

  const columns = [
    { key: 'created_at', label: 'Ingested', render: v => v ? new Date(v).toLocaleString() : '–' },
    { key: 'source_table', label: 'Source', render: v => SOURCE_LABELS[v] || v },
    { key: 'label', label: 'Item', render: v => (v ? (v.length > 90 ? v.slice(0, 90) + '…' : v) : '–') },
    { key: 'category', label: 'Category', render: v => v || '–' },
    { key: 'ref', label: 'Reference', render: v => v || '–' },
  ];

  return (
    <div className="tab-content">
      <p style={{ color: '#888', fontSize: 13, margin: '0 0 12px' }}>
        Newest ingested rows across all content tables (warning letters + drug / OTC / cosmetic indexes), newest first.
      </p>
      <DataTable columns={columns} data={items} emptyText="No ingested content yet." />
      {items.length > 0 && hasMore && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button className="btn-secondary" onClick={loadOlder} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load older'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ====================================================================
   Main: DataExplorer
   ==================================================================== */
function DataExplorer() {
  const { hasPermission } = useContext(AuthContext);
  // Warning Letters is an operational (mutating) surface — keep it gated on its own
  // permission so folding it into Data Explorer doesn't widen who can trigger scrapes.
  const canWarningLetters = !!(hasPermission && hasPermission('access:warning_letters'));
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = [
    { key: 'regulations', label: 'Regulations' },
    { key: 'keywords', label: 'Keywords' },
    { key: 'vectors', label: 'Vector Indexes' },
    { key: 'test-cases', label: 'Test Cases' },
    { key: 'seed-data', label: 'Seed Data' },
    { key: 'recent-content', label: 'Recent Ingested' },
    ...(canWarningLetters ? [{ key: 'warning-letters', label: 'Warning Letters' }] : []),
    { key: 'config', label: 'Config & Health' },
  ];

  // Deep-link support: /admin/data-explorer?tab=warning-letters (e.g. the redirect
  // from the retired /admin/warning-letters route). Fall back to the default tab if
  // the requested tab is unknown or not permitted for this user.
  const requested = searchParams.get('tab');
  const [tab, setTab] = useState(
    tabs.some(t => t.key === requested) ? requested : 'regulations'
  );

  const selectTab = (key) => {
    setTab(key);
    setSearchParams(key === 'regulations' ? {} : { tab: key }, { replace: true });
  };

  return (
    <div className="data-explorer-page">
      <h1>Data Explorer</h1>
      <div className="de-tab-bar">
        {tabs.map(t => (
          <button key={t.key} className={tab === t.key ? 'de-tab active' : 'de-tab'}
            onClick={() => selectTab(t.key)}>{t.label}</button>
        ))}
      </div>
      {tab === 'regulations' && <RegulationsTab />}
      {tab === 'keywords' && <KeywordDictionariesTab />}
      {tab === 'vectors' && <VectorIndexesTab />}
      {tab === 'test-cases' && <TestCasesTab />}
      {tab === 'seed-data' && <SeedDataTab />}
      {tab === 'recent-content' && <RecentContentTab />}
      {tab === 'warning-letters' && canWarningLetters && <WarningLettersPipeline />}
      {tab === 'config' && <SystemConfigTab />}
    </div>
  );
}

export default DataExplorer;
